import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Text } from '@teals/ui-web';
import {
  useCreateAgentMutation,
  useGetAgentsQuery,
  useGetDataSourcesQuery,
} from '../../store/api';
import { useAuth } from '../../hooks/useAuth';
import BuilderChat from './BuilderChat';
import {
  EMPTY_AGENT_CONFIG,
  type ProposedConfig,
} from './builderHelpers';

const Page = styled.div`
  max-width: 52rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const Subtitle = styled.p`
  margin: 0.35rem 0 0;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const CREATE_GREETING =
  "I'm the Agent Builder. Describe the agent you want — purpose, type, model, and any data source. I'll propose a configuration. Nothing is saved until you Accept. I will never ask for API secrets.";

const SEED_CHIPS = [
  'A support bot that answers from our FAQ',
  'Conversational agent',
  'Use gpt-4o',
  'Associate a data source',
];

const AgentBuilderCreate: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { data: catalog = [] } = useGetAgentsQuery();
  const { data: orgDataSources } = useGetDataSourcesQuery(undefined, {
    skip: !orgId,
  });
  const [createAgent] = useCreateAgentMutation();
  const [hasContent, setHasContent] = useState(false);

  useEffect(() => {
    if (!hasContent) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasContent]);

  const promptContext = useMemo(
    () => ({
      mode: 'create',
      edit_mode: false,
      read_only: false,
      catalog_agents: catalog.slice(0, 40).map((a) => ({
        agent_name: a.agentName,
        name: a.name,
        agent_type: a.agentType,
      })),
      catalog_data_sources: (orgDataSources || []).slice(0, 40).map((d) => ({
        data_source_id: d.dataSourceId,
        name: d.name,
      })),
    }),
    [catalog, orgDataSources],
  );

  const confirmLeave = useCallback(() => {
    if (!hasContent) return true;
    return window.confirm(
      'Nothing is saved yet. Leave without creating this agent?',
    );
  }, [hasContent]);

  const handleBack = () => {
    if (!confirmLeave()) return;
    navigate('/agents');
  };

  const handleAccept = async ({
    proposed,
    agentConfig,
    conversationId,
  }: {
    proposed: ProposedConfig;
    agentConfig: Record<string, unknown>;
    conversationId?: string;
  }) => {
    const created = await createAgent({
      ...agentConfig,
      conversation_id: conversationId,
      name: proposed.agentName || (agentConfig.agent_name as string),
    }).unwrap();
    navigate(
      `/agents/${encodeURIComponent(created.id)}?tab=configure&created=1`,
    );
  };

  return (
    <Page>
      <HeaderRow>
        <div>
          <Text variant="h3">Create agent</Text>
          <Subtitle>
            Chat to describe the agent. Accept creates a Draft. Discard or leave
            saves nothing.
          </Subtitle>
        </div>
        <Button variant="ghost" onClick={handleBack}>
          Back
        </Button>
      </HeaderRow>
      <Card padding={20} elevation="sm">
        <BuilderChat
          mode="create"
          greeting={CREATE_GREETING}
          subtitle="Propose a config in a few turns. Accept to save a Draft — nothing is persisted until then."
          placeholder="Describe the agent you want to build…"
          seedChips={SEED_CHIPS}
          promptContext={promptContext}
          metadataSource="create"
          editsEnabled
          currentConfig={EMPTY_AGENT_CONFIG}
          fleetNames={[]}
          dataSourceNames={[]}
          onAccept={handleAccept}
          onContentChange={setHasContent}
        />
      </Card>
    </Page>
  );
};

export default AgentBuilderCreate;
